import React from 'react'
import * as rn from 'react-native'

import * as c from '../../common'
import * as r from '../../react-utils'

import Feather from 'react-native-vector-icons/Feather'
import Slider from '@react-native-community/slider'
import { createNumberMask } from 'react-native-mask-input'

import * as gs from '../gStyles'

import Button from './Button'
import Field from './Field'
import Labeled from './Labeled'
import Radio from './Radio'
import Select from './Select'
import Text from './Text'
import TextInput from './TextInput'
import YesOrNo from './YesOrNo'

export type PayMethodProps = c.r<{
  customerID: string
}>

export default React.memo<PayMethodProps>(function PayMethod({ customerID }) {
  // c.debug('<PayMethod />')
  //#region styling
  const rowGap = gs.useRowGap('canvas')
  const gStyles = gs.useGlobalStyles('canvas')
  const t = r.useTheme()
  const styles = gs.useThemedStyleSheet(themedStyles, 'canvas')
  //#endregion
  //#region global
  const userID = c.getUserID()
  const admins = r.useEnv<c.Dict<boolean>>('admins')
  const isAdmin = !!admins?.[userID ?? '']
  const canOverrideUsers = r.useEnv<c.Dict<boolean>>('canOverride')
  const canOverride = isAdmin || !!canOverrideUsers?.[userID ?? '']

  const [customerName] = r.useCustomerField(customerID, 'customerName')
  const [security_contract_sign_customer] = r.useCustomerField(
    customerID,
    'security_contract_sign_customer',
  )
  const [security_monitoring_enabled] = r.useCustomerField(
    customerID,
    'security_monitoring_enabled',
  )
  const [commission_no_split] = r.useCustomerField(
    customerID,
    'commission_no_split',
  )
  const [dealSolarCompany] = r.useCustomerField(customerID, 'solarCompany')
  const [commission_rules_date] = r.useCustomerField(
    customerID,
    'commission_rules_date',
  )
  const [commission_split_threshold] = r.useCustomerField(
    customerID,
    'commission_split_threshold',
  )
  const monitoringMonthlyFee = security_monitoring_enabled === 'yes' ? 9.99 : 0
  const MAX_LOAN_COMMISSION = r.useEnv<number>('maxLoanCommission')

  const {
    mixedPricing,
    mixedAlarmPriceLocked,
    mixedPricingValid,
    mixedHomeImprovementPrice,
    mixedContractTotal,
    appliedLoan,
    appliedLoanApply,
    appliedLoanFinalCommission: finalCommission,
    appliedLoanFinalCommissionHandle,
    appliedLoanGrandTotalWDF: grandTotalWDF,
    appliedLoanGrandTotalWDFHandle,
    appliedLoanNoDataInputted: noDataInputted,
    appliedLoanUnlock,

    availLoanCompaniesOpts,
    loanCompany,
    loanCompanyName,
    loanCompanyNameHandle,

    availOffersOpts,
    offerName,
    offerNameHandle,

    availTermsOpts,
    term,
    termName,
    termNameHandle,

    availPlansOpts,
    planName,
    planNameHandle,

    paymentMethodSet,
    paymentMethodVal,
  } = r.usePayment(customerID!)
  //#endregion
  //#region local
  const paymentMethodHandle = React.useCallback(
    (newVal: c.PaymentMethod) => {
      paymentMethodSet(newVal)
    },
    [paymentMethodSet],
  )
  const multiplier = 1 / (1 - (term?.cost || 0))
  const gtStep = (50 / multiplier) * SLIDER_SCALE
  const [isDragging, isDraggingSet] = React.useState(false)
  const [dragVal, dragValSet] = React.useState(0)
  const [hideCommission, , toggleHideCommission] = r.useBool(true)
  const finalCommissionSliderVal = isDragging
    ? dragVal
    : finalCommission * SLIDER_SCALE
  const finalCommissionSlidingStartHandle = React.useCallback(() => {
    isDraggingSet(true)
    dragValSet(finalCommission * SLIDER_SCALE)
  }, [finalCommission])
  const finalCommissionSliderHandle = React.useCallback(
    (newUnscaledVal: number): void => {
      dragValSet(newUnscaledVal)
      appliedLoanFinalCommissionHandle(newUnscaledVal / SLIDER_SCALE)
    },
    [appliedLoanFinalCommissionHandle],
  )
  const finalCommissionSlidingCompleteHandle = React.useCallback(
    (newUnscaledVal: number): void => {
      isDraggingSet(false)
      appliedLoanFinalCommissionHandle(newUnscaledVal / SLIDER_SCALE)
    },
    [appliedLoanFinalCommissionHandle],
  )
  const finalCommissionDecrHandle = React.useCallback(() => {
    const newCommission = finalCommission - 50 / multiplier
    if (newCommission < 0) return
    appliedLoanFinalCommissionHandle(newCommission)
  }, [appliedLoanFinalCommissionHandle, finalCommission, multiplier])
  const finalCommissionIncrHandle = React.useCallback(() => {
    const newCommission = finalCommission + 50 / multiplier
    if (newCommission > (MAX_LOAN_COMMISSION || 0)) return
    appliedLoanFinalCommissionHandle(newCommission)
  }, [
    MAX_LOAN_COMMISSION,
    appliedLoanFinalCommissionHandle,
    finalCommission,
    multiplier,
  ])
  const applyHandle = React.useCallback(() => {
    appliedLoanApply()
  }, [appliedLoanApply])
  const unlockHandle = React.useCallback(() => {
    appliedLoanUnlock()
  }, [appliedLoanUnlock])
  const [overrideIs, , overrideIsToggle] = r.useBool(false)
  const overrideYesHandle = React.useCallback(() => {
    appliedLoanUnlock()
    overrideIsToggle()
  }, [appliedLoanUnlock, overrideIsToggle])
  const monthlyPayment = React.useMemo((): number => {
    if (appliedLoan) {
      const {
        grandTotalWDF: appliedGrandTotalWDF,
        termInterest,
        termYears,
      } = appliedLoan
      if (termInterest === 0) {
        return grandTotalWDF / termYears / 12
      }
      return c.calcLoanMonthlyPay(
        appliedGrandTotalWDF,
        termInterest * 100,
        termYears,
      )
    }

    if (!term) return 0
    if (term.interest === 0) {
      return grandTotalWDF / term.years / 12
    }

    return c.calcLoanMonthlyPay(grandTotalWDF, term.interest * 100, term.years)
  }, [appliedLoan, grandTotalWDF, term])

  const customGrandTotalWDFHandle = React.useCallback(
    (customGrandTotalWDFInCents: string) => {
      // $12.00 ->  '1200', in any case it's just cents instead of dollars
      // Twelve dollars = twelve hundred cents
      appliedLoanGrandTotalWDFHandle(Number(customGrandTotalWDFInCents) / 100)
    },
    [appliedLoanGrandTotalWDFHandle],
  )
  //#endregion

  const SliderWrapper = !term || !!appliedLoan ? rn.Pressable : rn.View

  const cashInputs = (
    <>
      {c.IS_MAIN && (
        <>
          {false && (
            <>
              {rowGap}
              <YesOrNo
                canBeEmpty
                disabled={!!appliedLoan}
                label="Will you include a 5% discount?"
                on="canvas"
                onPress={c.emptyFn}
                value="no"
              />
            </>
          )}
          {rowGap}
          <Field
            customerID={customerID}
            field="security_alarm_payout"
            on="canvas"
          />
          {rowGap}
          <Field
            customerID={customerID}
            disabled={!!appliedLoan}
            field="security_monitoring_enabled"
            on="canvas"
          />
        </>
      )}
    </>
  )

  const financingInputs = (
    <>
      {rowGap}
      <Select
        disabled={!!appliedLoan}
        label="Finance Company"
        on="canvas"
        onChange={loanCompanyNameHandle}
        opts={availLoanCompaniesOpts}
        value={loanCompanyName}
      />
      {rowGap}
      <Select
        disabled={!!appliedLoan}
        label="Offer"
        on="canvas"
        onChange={offerNameHandle}
        opts={availOffersOpts}
        value={offerName}
      />
      {rowGap}
      <Select
        disabled={!!appliedLoan}
        label="Plan"
        on="canvas"
        onChange={planNameHandle}
        opts={availPlansOpts}
        value={planName}
      />
      {rowGap}
      <Select
        disabled={!!appliedLoan}
        label="Term"
        on="canvas"
        onChange={termNameHandle}
        opts={availTermsOpts}
        value={termName}
      />
      {rowGap}
      {c.IS_MAIN && (
        <>
          <Field
            customerID={customerID}
            disabled={!term || !!appliedLoan}
            field="security_alarm_payout"
            on="canvas"
          />
          {rowGap}
          <Field
            customerID={customerID}
            disabled={!term || !!appliedLoan}
            field="security_monitoring_enabled"
            on="canvas"
          />
          {rowGap}
        </>
      )}

      <rn.View style={gStyles.separatorH} />

      <rn.View style={styles.selectedLoan}>
        {!!loanCompanyName && (
          <>
            <Text centerText on="canvas" size={12}>
              {loanCompany?.contractorID
                ? `Contractor / Dealer #: ${loanCompany.contractorID}`
                : '\0'}
            </Text>
            {gs.rowGap8}
          </>
        )}
        <Text centerText on="canvas" size={18}>
          {term?.desc || '\0'}
        </Text>
        {gs.rowGap8}
        <Text centerText on="canvas" size={18}>
          {term?.desc ? `$${monthlyPayment.toFixed(0)}/mo` : '\0'}
        </Text>
        {gs.rowGap8}
        <Text centerText on="canvas" size={12}>
          {term?.loanCode ? `Loan Code: ${term.loanCode}` : '\0'}
        </Text>
      </rn.View>
    </>
  )

  if (overrideIs) {
    return (
      <rn.View style={r.isMobile && gStyles.content}>
        <Text centerText on="canvas" size={18}>
          Are you sure you want to override the loan for the already signed
          customer {customerName}?
        </Text>

        {gs.rowGap16}

        <rn.View style={styles.buttons}>
          <Button on="canvas" onPress={overrideYesHandle}>
            Yes, override⚠️
          </Button>

          {rowGap}

          <Button on="canvas" onPress={overrideIsToggle}>
            No, go back
          </Button>
        </rn.View>
      </rn.View>
    )
  }

  const Wrapper = r.isWeb ? rn.View : React.Fragment

  return (
    <rn.View style={r.isMobile && gStyles.content}>
      <Wrapper style={gStyles.contentHorizontal}>
        {rowGap}
        <Radio
          disabled={!!appliedLoan}
          grow
          label="How will the customer pay?"
          on="canvas"
          //@ts-expect-error TO-DO
          onChange={paymentMethodHandle}
          opts={r.PAYMENT_METHOD_OPTS}
          value={appliedLoan?.paymentMethod || paymentMethodVal}
        />

        {paymentMethodVal === 'cash' ? cashInputs : financingInputs}
      </Wrapper>

      {gs.rowGap16}
      <rn.View style={gStyles.separatorH} />
      {gs.rowGap16}

      <TextInput
        editable={!appliedLoan && !mixedAlarmPriceLocked}
        keyboardType="decimal-pad"
        mask={usdMask}
        label={mixedPricing ? 'Alarm Grand total' : 'Grand total'}
        on="canvas"
        onChangeText={customGrandTotalWDFHandle}
        // Accepts values in cents, so need to convert from dollars
        value={((appliedLoan?.grandTotalWDF || grandTotalWDF) * 100).toFixed(0)}
      />

      {mixedPricing && (
        <>
          {gs.rowGap16}
          <Text centerText on="canvas" size={14}>
            {`Home improvement: ${c.money(
              mixedHomeImprovementPrice,
            )} · Contract total: ${c.money(mixedContractTotal)}`}
          </Text>
          <Text centerText on="canvas" size={12}>
            {paymentMethodVal === 'cash'
              ? 'Alarm fees apply only to the alarm. Home improvement prices stay fixed.'
              : 'This loan finances the alarm only. Home improvement prices stay fixed.'}
          </Text>
          {!mixedPricingValid && (
            <Text centerText on="canvas" size={14}>
              Alarm total cannot be allocated without changing locked prices.
            </Text>
          )}
        </>
      )}

      {monitoringMonthlyFee > 0 && (
        <>
          {gs.rowGap16}
          <Text centerText on="canvas" size={14}>
            {`Monitoring: $${monitoringMonthlyFee.toFixed(2)}/mo`}
          </Text>
        </>
      )}

      {gs.rowGap16}
      <rn.View style={gStyles.separatorH} />
      {gs.rowGap16}

      <rn.View
        style={{
          paddingHorizontal: Number(gStyles.contentHorizontal.gap) || 16,
          width: '100%',
        }}
      >
        <rn.View
          style={{
            alignItems: 'center',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            paddingTop: 4,
            paddingBottom: 16,
          }}
        >
          <Feather
            color={t.canvas.opaque}
            name={hideCommission ? 'chevron-down' : 'chevron-up'}
            onPress={toggleHideCommission}
            size={24}
          />
        </rn.View>

        {!hideCommission && (
          <>
            {gs.rowGap16}
            <rn.View style={gs.rowSpaceBetween100}>
              <Labeled alignCenter grow label="Company Profit" on="canvas">
                <Text on="canvas" size={14}>
                  {Intl.NumberFormat('en-US', {
                    currency: 'USD',
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                    style: 'currency',
                  }).format(
                    c.repCommission(
                      appliedLoan?.finalCommission ?? finalCommission,
                      c.splitThresholdForDeal({
                        key: customerID,
                        rulesDate: commission_rules_date,
                        stampedThreshold: commission_split_threshold,
                        state: c.getCompanyState(dealSolarCompany || '') || '',
                      }),
                      commission_no_split,
                    ),
                  )}
                </Text>
              </Labeled>
            </rn.View>

            {gs.rowGap16}
            <rn.View style={gStyles.separatorH} />
            {gs.rowGap16}

            <rn.View style={gs.rowSpaceBetween100}>
              <SliderWrapper
                disabled={!term || !!appliedLoan}
                style={gs.width82}
              >
                <Slider
                  disabled={!term || !!appliedLoan}
                  maximumTrackTintColor={t.canvas.opaque}
                  maximumValue={(MAX_LOAN_COMMISSION || 0) * SLIDER_SCALE}
                  minimumTrackTintColor={
                    !term || !!appliedLoan
                      ? t.canvas.opaque
                      : t.canvas.highlight
                  }
                  minimumValue={0}
                  onSlidingComplete={finalCommissionSlidingCompleteHandle}
                  onSlidingStart={finalCommissionSlidingStartHandle}
                  onValueChange={finalCommissionSliderHandle}
                  step={gtStep}
                  style={gs.width100}
                  thumbTintColor={
                    !term || !!appliedLoan
                      ? t.canvas.opaque
                      : t.canvas.highlight
                  }
                  value={finalCommissionSliderVal}
                />
              </SliderWrapper>

              <Feather
                color={
                  !term || !!appliedLoan ? t.canvas.faint : t.canvas.highlight
                }
                name="minus"
                onPress={
                  !term || !!appliedLoan ? undefined : finalCommissionDecrHandle
                }
                size={20}
                style={styles.controlIcon}
              />

              <Feather
                color={
                  !term || !!appliedLoan ? t.canvas.faint : t.canvas.highlight
                }
                name="plus"
                onPress={
                  !term || !!appliedLoan ? undefined : finalCommissionIncrHandle
                }
                size={20}
                style={styles.controlIcon}
              />
            </rn.View>

            {gs.rowGap16}
            <rn.View style={gStyles.separatorH} />
            {gs.rowGap16}
          </>
        )}
      </rn.View>

      {!!security_contract_sign_customer && (
        <>
          <rn.View style={styles.alreadySigned}>
            <Feather color={t.warning} name="alert-triangle" size={16} />
            {gs.colGap8}
            <Text on="canvas" size={16}>
              Customer has already signed
            </Text>
          </rn.View>
          {gs.rowGap16}
        </>
      )}

      <rn.View style={styles.buttons}>
        <Button
          disabled={
            !term ||
            !!appliedLoan ||
            noDataInputted ||
            !mixedPricingValid ||
            (!!security_contract_sign_customer && !canOverride)
          }
          on="canvas"
          onPress={applyHandle}
        >
          Apply
        </Button>

        {rowGap}

        <Button
          disabled={
            !appliedLoan || (!!security_contract_sign_customer && !canOverride)
          }
          on="canvas"
          onPress={
            canOverride && !!appliedLoan && !!security_contract_sign_customer
              ? overrideIsToggle
              : unlockHandle
          }
        >
          {canOverride && !!appliedLoan && !!security_contract_sign_customer
            ? 'Override⚠️'
            : 'Unlock'}
        </Button>
      </rn.View>
    </rn.View>
  )
})

const themedStyles = gs.ThemedStyleSheet.create((t, on) => {
  const grandTotalWDF = {
    borderRadius: t.input.canvas.borderRadius,
    fontFamily: t.canvas.fontFamily,
    marginRight: -c.parseInt(t.input.canvas.paddingHorizontal),
    marginVertical: -c.parseInt(t.input.canvas.paddingVertical),
    paddingRight: t.input.canvas.paddingHorizontal,
    paddingVertical: c.parseInt(t.input.canvas.paddingVertical) / 2,
    textAlign: 'right' as const,
  }
  const textAlignRight = {
    color: t[on].color,
    fontFamily: t[on].fontFamily,
    textAlign: 'center' as const,
  }

  return {
    alignToRight: { alignItems: 'flex-end', justifyContent: 'space-between' },
    alreadySigned: { ...gs.rowCentered, justifyContent: 'center' },
    buttons: { ...gs.rowSpaceAround100, flexWrap: 'wrap', gap: t[on].gap },
    controlIcon: { ...gs.width12, textAlign: 'right' },
    customGrandTotalWDFLock: { ...gs.absolute, left: -24, top: 0 },
    grandTotalWDF,
    grandTotalWDFUnlocked: {
      ...grandTotalWDF,
      backgroundColor: t.input.canvas.backgroundColor,
    },
    selectedLoan: { ...gs.alignItemsCenter, flexGrow: 1 },
    textAlignRight,
    textAlignRightWarn: { ...textAlignRight, color: t.warning },
  }
})

/**
 * Slider library badly designed forces inner representation onto external
 * values (via the `onValueChange` prop), interfering with floating point
 * precision. Scaling helps avoids this.
 */
const SLIDER_SCALE = 100000

const usdMask = createNumberMask({
  //@ts-ignore
  allowDecimal: true, // ← allows user to type the '.' character
  delimiter: ',', // thousands separator
  precision: 2, // fixed 2 decimal places
  prefix: ['$'], // or ['$ '] for space after dollar
  // The next two are important for allowing real decimal input:
  requireDecimal: false, // ← decimal part is optional (user can skip it)
  separator: '.', // decimal separator
})
